import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Plus, Target, CalendarIcon, Trash2, RefreshCw, 
  TrendingUp, Clock, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconOptions = [
  { value: 'target', icon: Target },
  { value: 'wallet', icon: Wallet },
  { value: 'trending-up', icon: TrendingUp },
  { value: 'clock', icon: Clock },
];

const colorOptions = [
  { value: '#00e5a0', name: 'Émeraude' },
  { value: '#4f8eff', name: 'Bleu' },
  { value: '#7c3aed', name: 'Violet' },
  { value: '#f97316', name: 'Orange' },
  { value: '#fbbf24', name: 'Jaune' },
];

const ObjectivesPage = () => {
  const { token } = useAuth();
  const [objectives, setObjectives] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: 'target',
    color: '#00e5a0',
    target_amount: '',
    target_date: null,
    linked_account: ''
  });
  const [transactionAmount, setTransactionAmount] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const handleResetObjectives = async () => {
    try {
      await axios.delete(`${API}/reset/objectives`, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    // Also clear goal_overrides from finances store (cumulative envelope amounts)
    try {
      const r = await axios.get(`${API}/store/finances`, { headers: { Authorization: `Bearer ${token}` } });
      const finances = r.data || {};
      if (finances.goal_overrides) {
        delete finances.goal_overrides;
        await axios.put(`${API}/store/finances`, finances, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch {}
    setShowResetConfirm(false);
    toast.success('Données réinitialisées avec succès');
    window.location.reload();
  };

  const fetchData = async () => {
    try {
      const [objectivesRes, accountsRes, financesRes, patrimoineRes] = await Promise.all([
        axios.get(`${API}/objectives`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/store/finances`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/store/patrimoine`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
      ]);
      const overrides = (financesRes.data && financesRes.data.goal_overrides) || {};
      // Aggregate allocations linked to objectives across every savings account.
      // Each allocation contributes its amount to the matching objective.
      const savings = Array.isArray(patrimoineRes.data?.savings) ? patrimoineRes.data.savings : [];
      const allocByObjective = {};
      savings.forEach(s => {
        (s.allocations || []).forEach(a => {
          if (!a.objectiveId) return;
          const amt = parseFloat(a.amount) || 0;
          if (amt <= 0) return;
          if (!allocByObjective[a.objectiveId]) allocByObjective[a.objectiveId] = { total: 0, items: [] };
          allocByObjective[a.objectiveId].total += amt;
          allocByObjective[a.objectiveId].items.push({ account: s.name, name: a.name, amount: amt });
        });
      });
      // Apply envelope cumulative overrides + allocation amounts on top of objective.current_amount.
      const objs = objectivesRes.data.map(o => {
        const envelopeAmount = overrides[o.id] || 0;
        const alloc = allocByObjective[o.id] || { total: 0, items: [] };
        let current = o.current_amount || 0;
        if (envelopeAmount > 0) current += envelopeAmount;
        current += alloc.total;
        return {
          ...o,
          current_amount: current,
          envelope_amount: envelopeAmount > 0 ? envelopeAmount : undefined,
          allocation_total: alloc.total,
          allocation_items: alloc.items,
        };
      });
      setObjectives(objs);
      setAccounts(accountsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        target_date: formData.target_date ? format(formData.target_date, 'yyyy-MM-dd') : null,
        linked_account: formData.linked_account && formData.linked_account !== 'none' ? formData.linked_account : null
      };
      await axios.post(`${API}/objectives`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Objectif créé !');
      setDialogOpen(false);
      setFormData({
        name: '',
        icon: 'target',
        color: '#00e5a0',
        target_amount: '',
        target_date: null,
        linked_account: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/objectives/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Objectif supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddTransaction = async () => {
    if (!selectedObjective || !transactionAmount) return;
    try {
      await axios.post(
        `${API}/transactions`,
        {
          objective_id: selectedObjective.id,
          type: 'deposit',
          amount: parseFloat(transactionAmount),
          date: format(new Date(), 'yyyy-MM-dd')
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transaction ajoutée !');
      setTransactionDialogOpen(false);
      setTransactionAmount('');
      setSelectedObjective(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const calculateMonthlyNeeded = (objective) => {
    if (!objective.target_date) return null;
    const remaining = objective.target_amount - objective.current_amount;
    const targetDate = new Date(objective.target_date);
    const now = new Date();
    const monthsDiff = (targetDate.getFullYear() - now.getFullYear()) * 12 + 
                       (targetDate.getMonth() - now.getMonth());
    if (monthsDiff <= 0) return remaining;
    return remaining / monthsDiff;
  };

  const calculateEstimatedDate = (objective) => {
    if (objective.current_amount >= objective.target_amount) return 'Atteint !';
    // Estimate based on average monthly contribution (simplified)
    const remaining = objective.target_amount - objective.current_amount;
    const avgMonthly = objective.current_amount > 0 ? objective.current_amount / 3 : 100; // Assume 3 months
    const monthsNeeded = Math.ceil(remaining / avgMonthly);
    return `~${monthsNeeded} mois`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-8 h-8 text-cap-emerald animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="objectives-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-white mb-1">
            Objectifs Financiers
          </h1>
          <p className="text-cap-text-secondary">
            Suivez vos objectifs et atteignez vos rêves
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 text-xs" onClick={() => setShowResetConfirm(true)} data-testid="reset-objectives-btn"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Réinitialiser</Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover"
              data-testid="create-objective-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvel objectif
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Créer un objectif</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-cap-text-secondary">Nom de l'objectif</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Apport immobilier"
                  className="bg-cap-bg border-white/10 text-white"
                  required
                  data-testid="objective-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-cap-text-secondary">Couleur</Label>
                  <Select
                    value={formData.color}
                    onValueChange={(value) => setFormData({ ...formData, color: value })}
                  >
                    <SelectTrigger className="bg-cap-bg border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-cap-bg-elevated border-white/10">
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: color.value }}
                            />
                            {color.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-cap-text-secondary">Montant cible (€)</Label>
                  <Input
                    type="number"
                    value={formData.target_amount}
                    onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                    placeholder="10000"
                    className="bg-cap-bg border-white/10 text-white"
                    required
                    min="1"
                    data-testid="objective-amount-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-cap-text-secondary">Date cible (optionnel)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left bg-cap-bg border-white/10 text-white hover:bg-white/5"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-cap-text-muted" />
                      {formData.target_date ? (
                        format(formData.target_date, 'PPP', { locale: fr })
                      ) : (
                        <span className="text-cap-text-muted">Sélectionner une date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-cap-bg-elevated border-white/10">
                    <Calendar
                      mode="single"
                      selected={formData.target_date}
                      onSelect={(date) => setFormData({ ...formData, target_date: date })}
                      locale={fr}
                      className="text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-cap-text-secondary">Compte lié (optionnel)</Label>
                <Select
                  value={formData.linked_account}
                  onValueChange={(value) => setFormData({ ...formData, linked_account: value })}
                >
                  <SelectTrigger className="bg-cap-bg border-white/10 text-white">
                    <SelectValue placeholder="Aucun compte lié" />
                  </SelectTrigger>
                  <SelectContent className="bg-cap-bg-elevated border-white/10">
                    <SelectItem value="none">Aucun</SelectItem>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover"
                data-testid="submit-objective-btn"
              >
                Créer l'objectif
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="bg-[#1a1d27] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Réinitialiser les données</DialogTitle></DialogHeader>
          <p className="text-cap-text-secondary text-sm mt-2">Êtes-vous sûr de vouloir réinitialiser toutes les données de cette section ? Cette action est irréversible.</p>
          <div className="flex items-center justify-end gap-3 mt-4">
            <Button variant="outline" size="sm" className="border-white/10 text-cap-text-muted hover:text-white" onClick={() => setShowResetConfirm(false)}>Annuler</Button>
            <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={handleResetObjectives} data-testid="confirm-reset-objectives">Réinitialiser</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Objectives Grid */}
      {objectives.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {objectives.map((objective, index) => {
            const progress = (objective.current_amount / objective.target_amount) * 100;
            const monthlyNeeded = calculateMonthlyNeeded(objective);
            
            return (
              <motion.div
                key={objective.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 group"
                data-testid={`objective-card-${objective.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${objective.color}20` }}
                  >
                    <Target className="w-6 h-6" style={{ color: objective.color }} />
                  </div>
                  <button
                    onClick={() => handleDelete(objective.id)}
                    className="p-2 rounded-lg text-cap-text-muted hover:text-cap-error hover:bg-cap-error/10 opacity-0 group-hover:opacity-100 transition-all"
                    data-testid={`delete-objective-${objective.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <h3 className="font-display text-lg font-semibold text-white mb-2">
                  {objective.name}
                </h3>

                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-cap-text-secondary">
                      {formatCurrency(objective.current_amount)}
                    </span>
                    <span className="text-cap-text-muted">
                      {formatCurrency(objective.target_amount)}
                    </span>
                  </div>
                  <div className="relative h-4 bg-cap-bg rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(progress, 100)}%`,
                        backgroundColor: objective.color
                      }}
                    />
                    <span 
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold"
                      style={{ color: progress > 50 ? '#0d0f14' : objective.color }}
                    >
                      {progress.toFixed(0)}%
                    </span>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {objective.envelope_amount > 0 && (
                    <div className="flex items-center justify-between text-cap-text-secondary">
                      <span className="flex items-center gap-1"><Wallet className="w-3.5 h-3.5 text-cap-emerald" /> Via épargne liée</span>
                      <span className="text-cap-emerald font-medium">{formatCurrency(objective.envelope_amount)}</span>
                    </div>
                  )}
                  {objective.allocation_total > 0 && (
                    <div className="space-y-1" data-testid={`objective-allocations-${objective.id}`}>
                      <div className="flex items-center justify-between text-cap-text-secondary">
                        <span className="flex items-center gap-1"><Wallet className="w-3.5 h-3.5 text-cap-blue" /> Via allocations</span>
                        <span className="text-cap-blue font-medium">{formatCurrency(objective.allocation_total)}</span>
                      </div>
                      {(objective.allocation_items || []).map((it, i) => (
                        <div key={i} className="flex items-center justify-between text-xs text-cap-text-muted pl-5">
                          <span className="truncate">{it.account} · {it.name}</span>
                          <span className="shrink-0 ml-2">{formatCurrency(it.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {objective.target_date && (
                    <div className="flex items-center justify-between text-cap-text-secondary">
                      <span>Date cible</span>
                      <span>{format(new Date(objective.target_date), 'dd MMM yyyy', { locale: fr })}</span>
                    </div>
                  )}
                  {monthlyNeeded && monthlyNeeded > 0 && (
                    <div className="flex items-center justify-between text-cap-text-secondary">
                      <span>Effort mensuel</span>
                      <span style={{ color: objective.color }}>{formatCurrency(monthlyNeeded)}/mois</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-cap-text-secondary">
                    <span>Estimation</span>
                    <span>{calculateEstimatedDate(objective)}</span>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    setSelectedObjective(objective);
                    setTransactionDialogOpen(true);
                  }}
                  className="w-full mt-4 bg-white/5 border border-white/10 text-white hover:bg-white/10"
                  data-testid={`add-transaction-${objective.id}`}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une transaction
                </Button>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="glass-card p-12 text-center">
          <Target className="w-16 h-16 text-cap-text-muted mx-auto mb-4" />
          <h3 className="font-display text-xl font-semibold text-white mb-2">
            Aucun objectif défini
          </h3>
          <p className="text-cap-text-secondary mb-6 max-w-md mx-auto">
            Créez votre premier objectif financier pour commencer à suivre votre progression.
          </p>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover"
          >
            <Plus className="w-5 h-5 mr-2" />
            Créer mon premier objectif
          </Button>
        </div>
      )}

      {/* Transaction Dialog */}
      <Dialog open={transactionDialogOpen} onOpenChange={setTransactionDialogOpen}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Ajouter une transaction
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <p className="text-cap-text-secondary">
              Objectif: <span className="text-white">{selectedObjective?.name}</span>
            </p>
            <div className="space-y-2">
              <Label className="text-cap-text-secondary">Montant (€)</Label>
              <Input
                type="number"
                value={transactionAmount}
                onChange={(e) => setTransactionAmount(e.target.value)}
                placeholder="100"
                className="bg-cap-bg border-white/10 text-white"
                min="1"
                data-testid="transaction-amount-input"
              />
            </div>
            <Button
              onClick={handleAddTransaction}
              className="w-full bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover"
              disabled={!transactionAmount}
              data-testid="submit-transaction-btn"
            >
              Valider
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ObjectivesPage;
